import { useState } from 'react';
import { Shield, Eye, EyeOff, LogIn, ChevronLeft } from 'lucide-react';
import { Loader } from './ui/Loader';

interface AdminLoginProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onBackToNormal: () => void;
  onSwitchToSubAdmin?: () => void;
  /** When true, show a message that verification failed and they can try logging in again */
  verifyError?: boolean;
}

export function AdminLogin({ onLogin, onBackToNormal, onSwitchToSubAdmin, verifyError }: AdminLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onLogin(email, password);
    } catch (err: any) {
      setError(err.message || 'Feil ved innlogging');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl text-center mb-2">
          <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent pb-1">
            Talentfunn
          </span>
        </h1>
        <p className="text-center text-gray-600 mb-8">Admin-portal</p>

        {verifyError && (
          <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center">
            Kunne ikke verifisere tilgang. Logg inn på nytt.
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-post
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="din@epost.no"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Passord
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-12"
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={loading}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
          >
            {loading ? (
              <Loader size="md" className="text-white" />
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                Logg inn
              </>
            )}
          </button>
        </form>

        {/* Back to Normal Login */}
        <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
          {onSwitchToSubAdmin && (
            <button
              onClick={onSwitchToSubAdmin}
              className="w-full text-purple-600 hover:text-purple-800 transition-colors text-sm"
            >
              Logg inn som sub-admin
            </button>
          )}
          <button
            onClick={onBackToNormal}
            className="w-full text-gray-600 hover:text-gray-900 transition-colors text-sm flex items-center justify-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Tilbake til vanlig innlogging
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          All aktivitet blir logget og overvåket
        </p>
      </div>
    </div>
  );
}
